import { Visibility, VisibilityOff } from "@mui/icons-material"; // For password visibility toggle
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { AiFillCheckCircle } from "react-icons/ai";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  resetPassword,
  sendVerificationCode,
  verifyCode,
} from "../../redux/Actions/forgetPasswordActions"; // Import Redux actions
import { CLEAR_MESSAGES } from "../../redux/Actions/types"; // Assuming you have a clear messages action type
import Meta from "../Meta/Meta";

const ForgotPassword = ({ onClose }) => {
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitted, setSubmitted] = useState(false); // Track form submission
  const [loading, setLoading] = useState(false); // Track loading state
  const [step, setStep] = useState(1); // Track which step the user is in
  const [showPassword, setShowPassword] = useState(false); // Track password visibility
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // Track confirm password visibility

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  // Password strength state (mirror Signup)
  const [strengthVisible, setStrengthVisible] = useState(false);
  const [strength, setStrength] = useState({});

  const checkPasswordStrength = (password) => {
    setStrengthVisible(password.length > 0);
    setStrength({
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSymbol: /[^A-Za-z0-9]/.test(password),
    });
  };

  // Access the error and success messages from Redux state
  const { error: reduxError, success: reduxSuccess } = useSelector(
    (state) => state.forgetPassword
  );

  // Handle sending the verification code
  const handleEmailSubmit = () => {
    if (!email.trim()) {
      return alert(t("forgotPassword.validation.emailRequired"));
    }
    setLoading(true); // Start loading
    setSubmitted(true); // Mark form as submitted
    dispatch(sendVerificationCode(email.trim())).finally(() => {
      setLoading(false); // Stop loading after request completes
    });
  };

  // Handle verification code submission
  const handleVerifyCode = () => {
    if (!verificationCode.trim()) {
      return alert(t("forgotPassword.validation.codeRequired"));
    }
    setLoading(true);
    dispatch(verifyCode(email.trim(), verificationCode.trim()))
      .then(() => {
        setStep(3); // Move to the next step after verifying the code
        dispatch({ type: CLEAR_MESSAGES }); // Clear success/error messages when moving to the next step
      })
      .finally(() => setLoading(false));
  };

  // Handle password reset
  const handleResetPassword = () => {
    if (!email.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      return alert(t("forgotPassword.validation.allRequired"));
    }
    if (newPassword !== confirmPassword) {
      return alert(t("forgotPassword.validation.passwordMismatch"));
    }
    setLoading(true);
    dispatch(resetPassword(email.trim(), newPassword)) // Call the reset password action
      .finally(() => {
        setLoading(false); // Stop loading after request completes
      });
  };

  // Handle closing the modal/card and clearing Redux messages
  const handleClose = () => {
    dispatch({ type: CLEAR_MESSAGES }); // Clear success/error messages
    if (onClose) onClose(); // Call onClose if provided (for closing the modal or card)
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword(!showConfirmPassword);

  // Safely extract the error message, ensuring it's only displayed after submission
  const errorMessage =
    submitted && reduxError
      ? typeof reduxError === "string"
        ? reduxError
        : reduxError?.message || "An unknown error occurred"
      : null;

  return (
    <>
      <Meta
        title={t("forgotPassword.metaTitle")}
        description={t("forgotPassword.metaDescription")}
        keywords={t("forgotPassword.metaKeywords")}
      />
      <Box className="forgot-password-container">
        {/* Step 1: Email submission */}
        {step === 1 && (
          <Box>
            <TextField
              label={t("forgotPassword.emailLabel")}
              variant="outlined"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
            />

            <Box sx={{ position: "relative", mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleEmailSubmit}
                disabled={loading || !email.trim()} // Disable when loading or empty
              >
                {t("forgotPassword.sendCode")}
              </Button>

              {/* Loading spinner */}
              {loading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </Box>
        )}

        {/* Step 2: Verification code submission */}
        {step === 2 && (
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                textAlign: "center",
                fontWeight: 600,
                color: "text.secondary",
              }}
            >
              {t("forgotPassword.codeTitle")}
            </Typography>
            <TextField
              label={t("forgotPassword.codeLabel")}
              variant="outlined"
              fullWidth
              margin="normal"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              required
            />
            <Box sx={{ position: "relative", mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleVerifyCode}
                disabled={loading || !verificationCode.trim()} // Disable when loading or empty
              >
                {t("forgotPassword.submitCode")}
              </Button>

              {loading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </Box>
        )}

        {/* Step 3: Reset password (include email field here) */}
        {step === 3 && (
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                textAlign: "center",
                fontWeight: 600,
                color: "text.secondary",
              }}
            >
              {t("forgotPassword.resetTitle")}
            </Typography>
            <TextField
              label={t("forgotPassword.emailLabel")}
              variant="outlined"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              label={t("forgotPassword.newPassword")}
              variant="outlined"
              type={showPassword ? "text" : "password"}
              fullWidth
              margin="normal"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                checkPasswordStrength(e.target.value);
              }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={togglePasswordVisibility}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
              required
            />
            {strengthVisible && (
              <Box sx={{ mb: 2 }}>
                <Typography color={strength.minLength ? "green" : "gray"}>
                  <AiFillCheckCircle /> {t("signup.passwordStrength.minLength")}
                </Typography>
                <Typography color={strength.hasUpperCase ? "green" : "gray"}>
                  <AiFillCheckCircle /> {t("signup.passwordStrength.uppercase")}
                </Typography>
                <Typography color={strength.hasLowerCase ? "green" : "gray"}>
                  <AiFillCheckCircle /> {t("signup.passwordStrength.lowercase")}
                </Typography>
                <Typography color={strength.hasNumber ? "green" : "gray"}>
                  <AiFillCheckCircle /> {t("signup.passwordStrength.number")}
                </Typography>
                <Typography color={strength.hasSymbol ? "green" : "gray"}>
                  <AiFillCheckCircle /> {t("signup.passwordStrength.symbol", { defaultValue: isArabic ? "رمز واحد على الأقل (مثل !@#$%)" : "At least one symbol (e.g., !@#$%)" })}
                </Typography>
              </Box>
            )}
            <TextField
              label={t("forgotPassword.confirmPassword")}
              variant="outlined"
              type={showConfirmPassword ? "text" : "password"}
              fullWidth
              margin="normal"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={toggleConfirmPasswordVisibility}>
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
              required
            />
            <Box sx={{ position: "relative", mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleResetPassword} // Call the handleResetPassword function
                disabled={
                  loading ||
                  !email.trim() ||
                  !newPassword.trim() ||
                  !confirmPassword.trim()
                } // Disable when loading or any field is empty
              >
                {t("forgotPassword.resetBtn")}
              </Button>

              {loading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </Box>
        )}

        {/* Display error from Redux, but only after submission */}
        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {/* Display success message from Redux */}
        {reduxSuccess && (
          <>
            <Alert severity="success" sx={{ mt: 2 }}>
              {reduxSuccess}
            </Alert>
          </>
        )}

        {/* Close button should always be displayed */}
        <Box display="flex" justifyContent="space-between" sx={{ mt: 2 }}>
          <Button variant="outlined" color="error" onClick={handleClose}>
            {t("forgotPassword.close")}
          </Button>

          {/* Display Next button only if there's a success message and not loading */}
          {reduxSuccess && !loading && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setStep(step + 1); // Go to the next step
                dispatch({ type: CLEAR_MESSAGES }); // Clear messages when navigating to the next step
              }}
            >
              {t("forgotPassword.next")}
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
};

export default ForgotPassword;
